import { Suspense } from 'react'
import { Skeleton } from '@insightai/ui'
import { Outlet } from 'react-router-dom'
import { FeatureErrorBoundary } from '../RouteErrorBoundary'
import { PrototypeBanner } from './PrototypeBanner'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'

/**
 * F0-60 / F0-61 — the console shell.
 *
 * Structure notes:
 *   · the skip link is the first tabbable element on every page (WCAG 2.4.1);
 *     without it a keyboard user traverses ~20 sidebar links to reach content
 *   · `<main id="main-content">` is the skip target and the single landmark
 *   · Suspense sits INSIDE main, so a lazily-loaded route chunk swaps the content
 *     area without unmounting the shell — the sidebar stays interactive during a
 *     route chunk fetch, which matters on the slow connections NFR-PE-001 targets
 *   · the error boundary is also inside, so a crashed feature leaves navigation
 *     usable
 */
export function AppShell() {
  return (
    <div className="flex min-h-screen flex-col bg-surface-base text-text-primary">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      <PrototypeBanner />
      <TopBar />

      <div className="flex min-h-0 flex-1">
        <Sidebar className="hidden md:flex" />

        <main id="main-content" tabIndex={-1} className="min-w-0 flex-1 overflow-y-auto p-6">
          <FeatureErrorBoundary feature="console">
            <Suspense fallback={<RouteSkeleton />}>
              <Outlet />
            </Suspense>
          </FeatureErrorBoundary>
        </main>
      </div>
    </div>
  )
}

function RouteSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy>
      <span className="sr-only" role="status">
        Loading view
      </span>
      <Skeleton className="h-7 w-56" />
      <Skeleton className="h-4 w-80" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    </div>
  )
}
